import React from 'react';

const labelStyle = (theme) => ({
  display: 'block',
  marginBottom: 6,
  fontFamily: theme.typography?.fontFamily,
  fontSize: '0.72rem',
  letterSpacing: '0.06em',
  textTransform: 'uppercase',
  color: theme.colors.textMuted,
});

const humanKind = (entity) => {
  const kind = entity?.kind || entity?.entityKind || 'lore';
  return String(kind).replace(/-/g, ' ');
};

const summarizeBody = (bodyJson) => {
  if (!bodyJson || typeof bodyJson !== 'string') return '';
  let raw = bodyJson;
  try {
    const parsed = JSON.parse(bodyJson);
    if (typeof parsed === 'string') raw = parsed;
    else if (parsed && typeof parsed === 'object') {
      raw = parsed.body || parsed.description || parsed.text || parsed.notes || JSON.stringify(parsed);
    }
  } catch {
    raw = bodyJson;
  }
  return String(raw).trim().slice(0, 1200);
};

export const WorldLoreModal = ({
  theme,
  campaign,
  lore,
  migrationPreview,
  loading,
  previewLoading,
  onPromoteSource,
  onCopySourceToCampaign,
  onClose,
}) => {
  const c = theme.colors;
  const t = theme.typography || {};
  const entities = Array.isArray(lore?.entities) ? lore.entities : [];
  const noWorld = lore?.noWorld === true || (!campaign?.worldEntityId && !lore?.worldId);
  const candidates = Array.isArray(migrationPreview?.candidates)
    ? migrationPreview.candidates.slice(0, 8)
    : [];
  const canManageLore = campaign?.role === 'owner' || campaign?.role === 'dm';
  const [busyKey, setBusyKey] = React.useState('');

  const runCandidateAction = async (action, candidate) => {
    if (!candidate || !campaign?.id) return;
    const key = `${action}:${candidate.sourceType}:${candidate.sourceId}`;
    setBusyKey(key);
    try {
      if (action === 'promote' && onPromoteSource) {
        await onPromoteSource({
          campaignId: campaign.id,
          sourceType: candidate.sourceType,
          sourceId: candidate.sourceId,
        });
      } else if (action === 'copy' && onCopySourceToCampaign) {
        await onCopySourceToCampaign({
          campaignId: campaign.id,
          sourceType: candidate.sourceType,
          sourceId: candidate.sourceId,
        });
      }
    } finally {
      setBusyKey('');
    }
  };

  return (
    <div
      role="dialog"
      aria-label="World lore and atlas"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0,0,0,0.72)',
        zIndex: 200,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: 760,
          maxHeight: '82vh',
          overflow: 'auto',
          background: c.bgCardSolid,
          border: `1px solid ${c.borderPrimary}`,
          borderRadius: theme.effects?.borderRadiusLg || '12px',
          padding: 24,
          boxShadow: '0 18px 48px rgba(0,0,0,0.6)',
          color: c.textPrimary,
        }}
      >
        <div style={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 16,
          marginBottom: 18,
        }}>
          <div>
            <h2 style={{
              margin: '0 0 4px',
              fontFamily: t.headingFamily,
              fontSize: '1.3rem',
              color: c.accent,
            }}>World Lore / Atlas</h2>
            <div style={{
              fontFamily: t.fontFamily,
              color: c.textMuted,
              fontSize: '0.86rem',
            }}>{campaign?.name || 'Campaign'} visible lore</div>
          </div>
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '7px 11px',
              background: 'transparent',
              border: `1px solid ${c.borderLight}`,
              borderRadius: 4,
              color: c.textSecondary,
              cursor: 'pointer',
              fontFamily: t.fontFamily,
            }}
          >Close</button>
        </div>

        {canManageLore && (
          <div style={{
            borderTop: `1px solid ${c.borderLight}`,
            borderBottom: `1px solid ${c.borderLight}`,
            padding: '14px 0',
            marginBottom: 18,
          }}>
            <div style={labelStyle(theme)}>Associate lore</div>
            {noWorld ? (
              <div style={{
                color: c.textMuted,
                fontFamily: t.fontFamily,
                fontSize: '0.84rem',
                fontStyle: 'italic',
              }}>Attach this campaign to a world before associating lore.</div>
            ) : previewLoading ? (
              <div style={{
                color: c.textMuted,
                fontFamily: t.fontFamily,
                fontSize: '0.84rem',
                fontStyle: 'italic',
              }}>Scanning candidates...</div>
            ) : candidates.length === 0 ? (
              <div style={{
                color: c.textMuted,
                fontFamily: t.fontFamily,
                fontSize: '0.84rem',
                fontStyle: 'italic',
              }}>No eligible sources found.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {candidates.map((candidate) => {
                  const promoteDisabled = !!candidate.conflicts?.length || candidate.recommended?.recommendedAction === 'deferNpcLane';
                  const copyKey = `copy:${candidate.sourceType}:${candidate.sourceId}`;
                  const promoteKey = `promote:${candidate.sourceType}:${candidate.sourceId}`;
                  return (
                    <div
                      key={`${candidate.sourceType}:${candidate.sourceId}`}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr auto',
                        gap: 12,
                        alignItems: 'center',
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div style={{
                          fontFamily: t.headingFamily,
                          color: c.textPrimary,
                          fontSize: '0.94rem',
                          overflowWrap: 'anywhere',
                        }}>{candidate.name || 'Untitled source'}</div>
                        <div style={{
                          fontFamily: t.fontFamily,
                          color: c.textMuted,
                          fontSize: '0.76rem',
                          marginTop: 2,
                        }}>
                          {candidate.sourceLabel || candidate.sourceType}
                          {candidate.isPrivateSource ? ' - DM only' : ''}
                          {candidate.conflicts?.length ? ' - review existing World Lore match' : ''}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
                        <button
                          type="button"
                          onClick={() => runCandidateAction('copy', candidate)}
                          disabled={busyKey === copyKey || !onCopySourceToCampaign}
                          style={{
                            padding: '6px 9px',
                            borderRadius: 4,
                            border: `1px solid ${c.borderLight}`,
                            background: 'transparent',
                            color: c.textSecondary,
                            fontFamily: t.fontFamily,
                            fontSize: '0.76rem',
                            cursor: busyKey === copyKey ? 'wait' : 'pointer',
                            opacity: busyKey === copyKey ? 0.7 : 1,
                          }}
                        >Campaign only</button>
                        <button
                          type="button"
                          onClick={() => runCandidateAction('promote', candidate)}
                          disabled={promoteDisabled || busyKey === promoteKey || !onPromoteSource}
                          style={{
                            padding: '6px 9px',
                            borderRadius: 4,
                            border: `1px solid ${promoteDisabled ? c.borderLight : c.accent}`,
                            background: promoteDisabled ? 'transparent' : c.accent,
                            color: promoteDisabled ? c.textMuted : c.bgPrimary,
                            fontFamily: t.fontFamily,
                            fontSize: '0.76rem',
                            cursor: promoteDisabled ? 'not-allowed' : (busyKey === promoteKey ? 'wait' : 'pointer'),
                            opacity: busyKey === promoteKey ? 0.7 : 1,
                          }}
                        >Apply to World Lore</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {loading ? (
          <div style={{
            padding: '18px 0',
            color: c.textMuted,
            fontFamily: t.fontFamily,
            fontStyle: 'italic',
          }}>Loading lore...</div>
        ) : noWorld ? (
          <div style={{
            padding: '18px 0',
            color: c.textMuted,
            fontFamily: t.fontFamily,
            fontStyle: 'italic',
          }}>This campaign is not attached to a world yet.</div>
        ) : entities.length === 0 ? (
          <div style={{
            padding: '18px 0',
            color: c.textMuted,
            fontFamily: t.fontFamily,
            fontStyle: 'italic',
          }}>No world lore has been revealed for this campaign yet.</div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {entities.map((entity) => {
              const body = summarizeBody(entity.bodyJson);
              return (
                <div
                  key={`${entity.entityKind}:${entity.entityId}`}
                  style={{
                    borderTop: `1px solid ${c.borderLight}`,
                    paddingTop: 12,
                  }}
                >
                  <div style={labelStyle(theme)}>
                    {humanKind(entity)} {entity.revealLevel ? `- ${entity.revealLevel}` : ''}
                  </div>
                  <div style={{
                    fontFamily: t.headingFamily,
                    fontSize: '1rem',
                    color: c.textPrimary,
                    marginBottom: 5,
                  }}>{entity.name}</div>
                  {entity.summary && (
                    <div style={{
                      fontFamily: t.fontFamily,
                      color: c.textSecondary,
                      fontSize: '0.88rem',
                      lineHeight: 1.45,
                      marginBottom: body ? 8 : 0,
                    }}>{entity.summary}</div>
                  )}
                  {body && (
                    <div style={{
                      fontFamily: t.fontFamily,
                      color: c.textMuted,
                      fontSize: '0.82rem',
                      lineHeight: 1.45,
                      whiteSpace: 'pre-wrap',
                    }}>{body}</div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
